import { ChevronRight } from "lucide-react";
import type { Event } from "@/types";
import { formatDate } from "@shared/utils";

interface TicketEventCardProps {
  event: Partial<Event> & { id: string; name: string; image: string | null; dateevent: string };
  showArrow?: boolean;
  onClick?: () => void;
}

/**
 * Card displaying a single purchased event in the My Tickets page.
 * Upcoming events show a chevron and are clickable to open the QR viewer.
 * Past events are rendered at reduced opacity with no click action.
 */
export function TicketEventCard({
  event,
  showArrow = false,
  onClick,
}: TicketEventCardProps) {
  return (
    <button
      className={`flex w-full items-center gap-4 rounded-2xl bg-white/10 p-4 text-left backdrop-blur-sm transition-colors
        ${onClick ? "cursor-pointer hover:bg-white/20" : "cursor-default opacity-60"}`}
      onClick={onClick}
      disabled={!onClick}
    >
      {/* Event thumbnail */}
      {event.image ? (
        <img
          src={event.image}
          alt={event.name}
          className="h-[72px] w-[72px] flex-shrink-0 rounded-xl object-cover"
          onError={(e) => {
            e.currentTarget.style.visibility = "hidden";
          }}
        />
      ) : (
        <div className="h-[72px] w-[72px] flex-shrink-0 rounded-xl bg-white/20" />
      )}

      {/* Info */}
      <div className="flex flex-1 flex-col gap-1 overflow-hidden">
        <span
          className="truncate font-semibold text-white"
          style={{ fontSize: 14 }}
        >
          {event.name}
        </span>
        <span className="text-neutral-30" style={{ fontSize: 12 }}>
          {formatDate(event.dateevent)}
        </span>
        {event.location?.venue && (
          <span className="truncate text-neutral-30" style={{ fontSize: 12 }}>
            {event.location.venue}
          </span>
        )}
      </div>

      {/* Arrow — only for upcoming/clickable */}
      {showArrow && (
        <ChevronRight size={20} className="flex-shrink-0 text-secondary-120" />
      )}
    </button>
  );
}